import { Eye, FileUp, Trash2 } from 'lucide-react'

import { PersonalDocument } from '@/components/personal-document/personal-document'
import { PersonalDocumentList } from '@/components/personal-document/personal-document-list'
import { StatusBadge } from '@/components/status-badge'
import { PersonalDocuments } from '@/models/user'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../tooltip'

interface InsertPersonalDocumentProps {
  testId: string
  documents: PersonalDocuments[] | null
  handleDocuments: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleDrop: (event: React.DragEvent<HTMLLabelElement>) => void
  isDragging: boolean
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>
  handleRemoveDocument: (index: number) => void
}

export function InsertPersonalDocument({
  testId,
  documents,
  handleDocuments,
  handleDrop,
  isDragging,
  setIsDragging,
  handleRemoveDocument,
}: InsertPersonalDocumentProps) {
  return (
    <PersonalDocument.Root>
      <PersonalDocument.Title>Adicione documentos</PersonalDocument.Title>
      <PersonalDocument.Description>
        Incluir documento com foto válido, por exemplo, CNH ou identidade. Os
        documentos devem conter o número do CPF. Incluir também um comprovante
        de residência (contas de água, energia elétrica, etc).
      </PersonalDocument.Description>
      <PersonalDocument.RootContent>
        <PersonalDocument.Label
          isDragging={isDragging}
          htmlFor="add_documents"
          onDragOver={(e) => {
            e.preventDefault()
            setIsDragging(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            setIsDragging(false)
          }}
          onDrop={handleDrop}
          data-testid={`${testId}-add-documents-label`}
        >
          <FileUp
            size={60}
            className="text-monochromatic-300 group-hover:text-bios-primary"
          />
          <span className="text-center font-bold text-monochromatic-500 group-hover:text-bios-primary md:text-2xl">
            Toque para anexar <br /> os documentos
          </span>
          <span className="mt-4 text-sm text-monochromatic-300">
            Clique aqui ou arraste o arquivo
          </span>
          <input
            id="add_documents"
            type="file"
            multiple
            accept="image/*,application/pdf"
            className="hidden"
            onChange={handleDocuments}
          />
        </PersonalDocument.Label>
      </PersonalDocument.RootContent>
      <PersonalDocumentList.Root>
        {documents &&
          documents.length > 0 &&
          documents.map((file, index) => {
            return (
              <PersonalDocumentList.Content key={index} hasFile={!!file.file}>
                <div className="flex items-center gap-4">
                  <div className="max-w-52 truncate text-monochromatic-400 md:max-w-[40rem] 2xl:max-w-[50rem]">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>{file.file?.name}</span>
                        </TooltipTrigger>
                        <TooltipContent>{file.file?.name}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
                <div className="flex items-center gap-5 md:gap-8">
                  {file.f_status && <StatusBadge status={file.f_status} />}
                  {file.file && (
                    <Trash2
                      className="size-5 cursor-pointer text-monochromatic-400 md:size-7"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (file.file === null) return
                        handleRemoveDocument(index)
                      }}
                      data-testid={`${testId}-remove-document-button-${index}`}
                    />
                  )}
                  {file.url && (
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`${testId}-view-document-link-${index}`}
                    >
                      <Eye className="size-5 text-monochromatic-400 md:size-7" />
                    </a>
                  )}
                </div>
              </PersonalDocumentList.Content>
            )
          })}
      </PersonalDocumentList.Root>
    </PersonalDocument.Root>
  )
}
